import Tooltip from 'antd/lib/tooltip';
import Table from 'antd/lib/table';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Icon from 'antd/lib/icon';
import moment from 'moment';
import * as React from 'react';
import { fetchDocuments } from 'src/actions/shipmentExecutionActions';
import usePagination from 'src/actions/usePagination';
import { StylesProps } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { PAGINATION_REDUCERS } from 'src/reducers/paginationReducers';
import SEDocumentsFilterDrawer from './SEDocumentsFilterDrawer';
import SEDocumentsShareDrawer from './SEDocumentsShareDrawer';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import GenericHoc from 'src/components/hoc/GenericHoc';
import Loader from 'src/components/common/Loader';
import { useRef } from 'react';
import { Document } from '../SETasks/SETasks.interfaces';
import FileViewer from 'src/components/common/FileViewer';
import { message } from 'antd';
import { SearchDebounceMs, SearchMinLengthForApi } from 'src/Library/Constants';

const filterIcon = require('src/assets/images/filter.svg');

const styles = (theme) => ({
  search: {
    width: 170,
    '& .ant-input': {
      fontSize: 12,
    },
    '& .ant-input-suffix': {
      fontSize: 20,
    },
  },
  button: {
    borderRadius: '4px',
    backgroundColor: '#2A8FF7',
    color: 'white',
    fontSize: 12,
  },
  disabledButton: {
    '& .ant-btn': {
      borderRadius: '4px',
      backgroundColor: '#CCCCCC',
      color: 'white',
      fontSize: 12,
    },
  },
  hrElement: {
    border: 'none',
    borderTop: '1px double #EDEDED',
    color: '#EDEDED',
    margin: 10,
  },
  table: {
    margin: '5px 0',
    background: '#fff',
    cursor: 'pointer',
    '& .ant-table-align-left': {
      backgroundColor: '#d5d5d5',
    },
    '& .ant-table': {
      border: 'none',
    },
    '& .ant-table table': {
      borderSpacing: '0 5px',
    },
    '& .ant-table-tbody > tr > td': {
      borderBottom: 0,
    },
    '& .ant-table-thead > tr > th': {
      borderBottom: 0,
    },
    '& .ant-table-thead': {
      fontSize: '10px',
      whiteSpace: 'pre-line !important',
      color: '#666666',
      backgroundColor: '#FFFFFF',
      padding: 0,
    },
    '& .ant-table-row': {
      background: '#FCFAF4',
    },
  },
  tableRow: {
    margin: '5px 0',
  },
  textCol: {
    color: '#424242',
    fontSize: 12,
    fontWeight: 600,
    width: 160,
  },
  ellipsisClass: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  nameCol: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#006EC3',
    textDecoration: 'underline',
  },
});
export interface SEDocumentsProps extends StylesProps<ReturnType<typeof styles>> {
  shipmentId: number,
  companyFilter
}

const SEDocuments: React.FC<SEDocumentsProps> = ({
  classes,
  companyFilter,
  shipmentId,
}) => {
  const initialState = {
    sortSelected: {},
    sortDocumentsBy: {
      col: 'updatedAt',
      dir: 'desc',
    },
    searchQuery: '',
    selectedRowKeys: [],
    selectedRows: [] as any,
    isSearching: false,
    popupVisible: false,
    documents: [] as any,
    isDocumentsLoading: false,
    isFilterApplied: false,
    isShareDrawerVisible: false,
    isFilterDrawerVisible: false,
    isFileDrawerVisible: false,
  };

  const [state, setState] = useGenericState<typeof initialState>(initialState);
  const initialRenderer = useRef(true);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pagination = usePagination({
    paginationReducer: PAGINATION_REDUCERS.seDocuments,
    fetchAPI: fetchDocuments({ id: shipmentId }),
  });

  const downloadFilesInBulk = async () => {
    try {
      await Promise.all(
        state.selectedRows.map(async (row) => {
          const url =  row?.url;
          const response = await fetch(url);
          const blob = await response.blob();
          const filename = row?.fileName;
        
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        })
      )
    } catch (err) {
      message.error("Something went wrong while downloading Documents");
    }
  }

  const { documentTypeList, sourceObjectTypeList, organisationIdList,
    dateRange } = pagination.state.appliedFilters;

  React.useEffect(() => {
    if (shipmentId) {
      pagination.actions.resetFilter();
    }
  }, [shipmentId]);

  React.useEffect(() => {
    if (initialRenderer.current) {
      initialRenderer.current = false;
      return;
    }
    if (pagination.state.appliedFilters.searchQuery === state.searchQuery) {
      return;
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    const queryLength = state.searchQuery.trim().length;
    const shouldCallApi = queryLength === 0 || queryLength >= SearchMinLengthForApi.SEDocuments;
    if (!shouldCallApi) {
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      pagination.actions.setFilter({ searchQuery: state.searchQuery }, true);
    }, SearchDebounceMs.SEDocuments);
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, [state.searchQuery]);

  React.useEffect(() => {
    setState({ documents: pagination.state.data });
  }, [pagination.state.data]);

  React.useEffect(() => {
    setState({ 
      isFilterApplied: documentTypeList?.length || sourceObjectTypeList?.length
      || organisationIdList?.length || dateRange?.length ? true : false });
    
  }, [pagination.state.appliedFilters]);

  const handleSearch = e => {
    setState({ searchQuery: e.target.value})
  }

  const handleShareClick = () => {
    setState({
      isShareDrawerVisible: true,
    });
  };

  const [fileToPreview, setFileToPreview] = React.useState<{ url: string, title: string }>();
  const handlePreviewFile = (url: string, title: string) => {
    if (!url) {
      return;
    }

    setState({ isFileDrawerVisible: true });
    setFileToPreview({
      url,
      title,
    });
  }

  const renderFileName = (col, record) => {
    return (
      <div>
        <Tooltip title={record[col]}>
          <div className={classes.textCol}>
            <div
              onClick={() => {
                handlePreviewFile(record.url, record.fileName);
              }}
              className={`${classes.nameCol} ${classes.ellipsisClass}`}
            >
              {record[col]?.length ? record[col] : '-'}
            </div>
          </div>
        </Tooltip>
        <Tooltip title={record.documentType}>
          <div
            className={`${classes.textCol} ${classes.ellipsisClass}`}
            style={{ fontSize: 10, color: '#333333' }}
            >
            {record.documentType?.length ? record.documentType : '-'}
          </div>
        </Tooltip>
      </div>
    );
  };

  const renderUploadedByOrg = (col, record) => {
    return (
      <div>
        <div className={`${classes.textCol} ${classes.ellipsisClass}`}>
          {record[col]?.length ? record[col] : '-'}
        </div>
        <div
          className={`${classes.textCol} ${classes.ellipsisClass}`}
          style={{ fontSize: 10, color: '#333333' }}
        >
          {record.uploadedByUser?.length ? record.uploadedByUser : '-'}
        </div>
      </div>
    );
  };

  const renderUpdatedAt = (col, record) => {
    const date: any = `${moment(record.updatedAt).format('DD MMM YYYY')}`;
    const time: any = `${moment(record.updatedAt).format('HH:mm')} Hrs`;
    return (
      <Tooltip
        className={classes.textCol}
        style={{ display: 'flex', justifyContent: 'flex-start' }}
        title={<div>{record[col] ? moment(record[col]).fromNow() : '-'}</div>}
      >
        {date}
        <div style={{ fontSize: 10, color: '#333333' }}>{time}</div>
      </Tooltip>
    );
  };
  const handleDownload = async (url: string, fileName: string) => {
    try {
      // Fetch the file content
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const tempUrl = URL.createObjectURL(blob);
      
      // Create a temporary <a> element to trigger the download
      const link = window.document.createElement('a');  // Correct usage
      link.href = tempUrl;
      link.download = fileName;  // Specify the file name here
      window.document.body.appendChild(link);   // Append the link to the document
      link.click();                      // Programmatically click the link to start the download
      window.document.body.removeChild(link);   // Clean up by removing the link
      URL.revokeObjectURL(tempUrl);      // Clean up the object URL
    } catch (error) {
      console.error('Download failed:', error);
    }
  };
  const renderDownloadIcon = (record) => {
    return (
      <a onClick={() => handleDownload(record.url, record.fileName)}>
        <Icon type="download" style={{ fontSize: 20 }} />
      </a>
    );
  };

  const renderDefault = (col, record) => {
    return (
      <Tooltip title={record[col]}>
        <div className={`${classes.textCol} ${classes.ellipsisClass}`}>
          {record[col]?.length ? record[col] : '-'}
        </div>
      </Tooltip>
    );
  };

  const renderCol = (col, text, record) => {
    switch (col) {
      case 'fileName': return renderFileName(col, record);
      case 'uploadedByOrg': return renderUploadedByOrg(col, record);
      case 'updatedAt': return renderUpdatedAt(col, record);
      case 'downloadIcon': return renderDownloadIcon(record);
      default: return renderDefault(col, record);
    }
  };

  const columnLabels = {
    fileName: 'File Name',
    taskName: 'Task',
    uploadedByOrg: 'Uploaded By',
    // uploadedByUser: 'Uploaded By - User',
    updatedAt: 'Updated At',
    downloadIcon: '',
  };

  const getColumns = (columnLabels) => {
    const columns = Object.keys(columnLabels).map((col: any) => ({
      key: col,
      align: 'left',
      width: col === 'downloadIcon' ? 30 : 150,
      title: col !== 'downloadIcon' ? (() => {
        return <div
          style={{
            display: 'flex', justifyContent: 'flex-start', color: '#666666'
          }}>
          {columnLabels[col]}
        </div>;
      }) : null,
      render: (text, record) => renderCol(col, text, record),
    }));
    return columns;
  };

  const splitDocuments = (documentList: Document[]) => {
    if (!documentList) {
      return [];
    }

    let documentListToReturn: Document[] = [];
    
    documentList.forEach((doc, docIndex) => {
      const urls = doc.url.split(',');
      const fileNames = doc.fileName.split(',');
      const updatedAt = doc.updatedAtTimestamps.split('#');

      const tempDocumentList: Document[] = urls.map((url, index) => ({
        ...doc,
        fileName: fileNames[index],
        url,
        updatedAt: updatedAt[index],
        record: {
          key: `${doc.documentId || docIndex}-${index}` // Ensuring unique key using doc id (if exists) and index
        }
      }));
      
      documentListToReturn.push(...tempDocumentList);
    });
    
    return documentListToReturn;
};

const filterSearchedDocuments = () => {
  const query = pagination.state.appliedFilters.searchQuery?.trim() ?? '';

  // Filter out items whose taskName includes "Approve"
  const filteredDocs = state.documents?.filter((item) => {
    if (companyFilter.showApproveTaskDoc) {
      if (item.taskName?.toLowerCase().includes('approve')) {
        return false;
      }
    }
    // If there's a search query, filter by fileName, documentType or taskName
    if (query.length) {
      const q = query.toLowerCase();
      return (
        item.fileName?.toLowerCase().includes(q) ||
        item.documentType?.toLowerCase().includes(q) ||
        item.taskName?.toLowerCase().includes(q)
      );
    }

    // If there's no search query, include the item (unless it had 'Approve' in the name)
    return true;
  });

  return splitDocuments(filteredDocs);
};
  
  const handleOnClickFilter = () => {
    setState({ isFilterDrawerVisible: true });
  }

  const renderSearchBox = () => {
    const Search = Input.Search;
    return <div className={classes.search}>
      <Tooltip
        placement="top"
        title="Search Document Type or Task Name or File Name">
        <Search
          allowClear
          placeholder="Search"
          value={state.searchQuery}
          onChange={e => handleSearch(e)} />
      </Tooltip>
    </div>;
  };

  const renderBulkDownload = () => {
    return <Tooltip title={`${state.selectedRowKeys.length ? '' : 'Select documents to download'}`}>
      <Button

        className={state.selectedRowKeys.length ? classes.button : classes.disabledButton}
        onClick={downloadFilesInBulk}
        disabled={state.selectedRowKeys.length ? false : true}
      >
        Bulk Download
      </Button>
    </Tooltip>;
  }

  const renderShareButton = () => {
    return <Tooltip title={`${state.selectedRowKeys.length ? '' : 'Select documents to share'}`}>
      <Button
        className={state.selectedRowKeys.length ? classes.button : classes.disabledButton}
        onClick={handleShareClick}
        disabled={state.selectedRowKeys.length ? false : true}
      >
        Share
      </Button>
    </Tooltip>;
  }
  
  const renderTable = () => {
    const columns: any = getColumns(columnLabels);

    const rowSelection = {
      onChange: (selectedRowKeys, selectedRows) => {
        setState({ selectedRowKeys, selectedRows });
      },
    };
    if (pagination.state.isLoading) {
      return <Loader zIndex={1} style={{ marginLeft: '0px' }}/>;
    }
    return <Table
      rowSelection={rowSelection}
      pagination={false}
      rowKey={(record: any) => record.key}
      className={classes.table}
      rowClassName={(x, y) => classes.tableRow}
      columns={columns}
      size="small"
      dataSource={filterSearchedDocuments()}
    />;
  };

  const renderFilters = () => {
    return <div style={{ margin: '5px 0 0 25px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
      onClick={handleOnClickFilter}
    >
      <img src={filterIcon} />
      <span style={{ paddingLeft: 3, paddingTop: 10 }}>Filter</span>
    </div>;
  }

  const renderFilterDrawer = () => {
    return (
      <SEDocumentsFilterDrawer
        visible={state.isFilterDrawerVisible}
        handleClose={() => setState({ isFilterDrawerVisible: false })}
        handleApply={() => {
          pagination.actions.applyFilter();
          setState({ isFilterDrawerVisible: false });
        }}
        id={shipmentId}
        pagination={pagination}
      />
    );
  };

  const renderShareDrawer = () => {
    return (
      <SEDocumentsShareDrawer
        visible={state.isShareDrawerVisible}
        onClose={() => {
          setState({ isShareDrawerVisible: false });
        }}
        documents={state.selectedRows}
        shipmentId={shipmentId}
      />
    );
  };

  const renderFileDrawer = () => {
    return (
      <FileViewer
        url={fileToPreview?.url}
        title={fileToPreview?.title}
        onClose={() => {
          setState({ isFileDrawerVisible: false });
          setFileToPreview(undefined);
        }}
      />
    );
  }

  const renderLine = () => {
    return <hr className={classes.hrElement} />;
  };
  
  return <div style={{ minHeight: 600 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', margin: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start'}}>
        {renderSearchBox()}
        {renderFilters()}
      </div>
      <div>
        <span style={{marginRight: '5px'}}>{renderBulkDownload()}</span>
        {renderShareButton()}
      </div>
    </div>
    {renderLine()}
    {renderTable()}

    {state.isFilterDrawerVisible && renderFilterDrawer()}
    {state.isShareDrawerVisible && renderShareDrawer()}
    {state.isFileDrawerVisible && renderFileDrawer()}
  </div>;
}
const mapStateToProps = ({ masterData }) => ({
  
  companyFilter: masterData?.execution
});
const options: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
};

const SEDocumentsWithHOC = GenericHoc(options)(SEDocuments);
export default SEDocumentsWithHOC;